import type { ProctoringEventType, ProctoringLeaveReason } from '@/constants/enums'

export type MonitorAlertKind = 'no_face' | 'multiple_faces' | 'camera_off'

export type MonitorAlert = {
    kind: MonitorAlertKind
    message: string
    faceCount?: number
    countdownSeconds: number | null
}

export type InterviewStreamState = {
    cameraStream: MediaStream | null
    videoTrack: MediaStreamTrack | null
    audioTrack: MediaStreamTrack | null
    remoteAudioTrack: MediaStreamTrack | null
    screenTrack: MediaStreamTrack | null
    activeVideoTrack: MediaStreamTrack | null
    videoElement: HTMLVideoElement | null
}

export type InterviewStreamPluginContext = {
    streams: InterviewStreamState
    addProctoringEvent: (type: ProctoringEventType) => void
    requestTerminate: (reason: ProctoringLeaveReason) => void
    log: (message: string) => void
    setFaceCount: (count: number) => void
    showNoFaceWarning: (options?: { logProctoring?: boolean }) => void
    showMultipleFacesWarning: (count: number) => void
    clearFaceWarning: () => void
    showCameraOffWarning: () => void
    clearCameraOffWarning: () => void
    notifyScreenShareEnded: () => void
    isVideoActive: () => boolean
    isFaceMonitoringPaused: () => boolean
}

export interface InterviewStreamPlugin {
    id: string
    start: (ctx: InterviewStreamPluginContext) => void | Promise<void>
    stop: () => void
    onStreamsUpdated?: (ctx: InterviewStreamPluginContext) => void
}

export type InterviewStreamPluginRegistration = {
    plugin: InterviewStreamPlugin
    /** When false, the host skips start/onStreamsUpdated until re-enabled. Defaults to true. */
    enabled?: boolean
}

export type InterviewStreamPluginInput = InterviewStreamPlugin | InterviewStreamPluginRegistration

export function defineStreamPlugin(plugin: InterviewStreamPlugin, enabled = true): InterviewStreamPluginRegistration {
    return { plugin, enabled }
}

export function normalizeStreamPlugin(entry: InterviewStreamPluginInput): InterviewStreamPluginRegistration {
    if ('plugin' in entry) {
        return { plugin: entry.plugin, enabled: entry.enabled ?? true }
    }
    return { plugin: entry, enabled: true }
}

export type InterviewStreamHostPluginOptions = {
    camera?: boolean
    screenShare?: boolean
}

export type RecordingInitData = {
    uploadSessionId: string
    /** Azure block write URL; empty for gcs-compose. */
    blobWriteUrl?: string
    chunkDurationMs: number
    uploadProtocol?: 'azure-blocks' | 'gcs-compose'
    /** Recording quality the server picked (INT-1059); 1280×720 @ 30 fps, 800 kbps video / 128 kbps audio when missing. */
    videoBitsPerSecond?: number
    audioBitsPerSecond?: number
    videoWidth?: number
    videoHeight?: number
    videoFps?: number
}

/** What the browser tells recording/init so it can pick the recording bitrate (INT-1059). */
export type RecordingInitOptions = {
    /** Upload speed measured by the pre-interview connection check, if it ran on this page load. */
    uploadMbps?: number | null
}
